import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Divider, Empty, Spin } from "antd";
import { observer } from "mobx-react-lite";
import { useCallback, useEffect, useRef } from "react";
import MessageItem from "@/components/Chat/MessageItem";
import ThreadPreview from "@/components/Chat/ThreadPreview";
import { getRootStore } from "@/stores/RootStore";
import { ChannelType, MessageAuthorType } from "@/types/chat";
import styles from "@/styles/MessageList.module.less";
function formatDateSeparator(timestamp) {
    const date = new Date(timestamp);
    const now = new Date();
    const isToday = date.getDate() === now.getDate() &&
        date.getMonth() === now.getMonth() &&
        date.getFullYear() === now.getFullYear();
    if (isToday)
        return "Сегодня";
    const yesterday = new Date(now);
    yesterday.setDate(yesterday.getDate() - 1);
    const isYesterday = date.getDate() === yesterday.getDate() &&
        date.getMonth() === yesterday.getMonth() &&
        date.getFullYear() === yesterday.getFullYear();
    if (isYesterday)
        return "Вчера";
    return date.toLocaleDateString("ru-RU", {
        day: "numeric",
        month: "long",
        year: date.getFullYear() !== now.getFullYear() ? "numeric" : undefined,
    });
}
function isSameDay(ts1, ts2) {
    const d1 = new Date(ts1);
    const d2 = new Date(ts2);
    return (d1.getDate() === d2.getDate() &&
        d1.getMonth() === d2.getMonth() &&
        d1.getFullYear() === d2.getFullYear());
}
const MessageList = observer(({ channelId, threadRootId }) => {
    const { chatStore } = getRootStore();
    const scrollRef = useRef(null);
    const sentinelRef = useRef(null);
    const prevMessageCountRef = useRef(0);
    const isLoadingOlderRef = useRef(false);
    const messages = chatStore.activeMessages;
    const isLoading = chatStore.isMessagesLoading;
    const currentUserId = chatStore.currentUserId;
    // Use ref for isLoading to prevent IntersectionObserver recreation on loading state changes
    const isLoadingRef = useRef(false);
    isLoadingRef.current = isLoading;
    // Auto-scroll to bottom on new messages (append)
    useEffect(() => {
        const container = scrollRef.current;
        if (!container)
            return;
        const prevCount = prevMessageCountRef.current;
        const newCount = messages.length;
        if (newCount > prevCount && !isLoadingOlderRef.current) {
            // New messages appended at bottom - scroll down
            container.scrollTop = container.scrollHeight;
        }
        isLoadingOlderRef.current = false;
        prevMessageCountRef.current = newCount;
    }, [messages.length]);
    // IntersectionObserver for loading older messages
    // NOTE: isLoading is accessed via ref to avoid recreating the observer on every loading toggle
    const handleLoadOlder = useCallback(() => {
        if (isLoadingRef.current || !channelId || threadRootId)
            return;
        isLoadingOlderRef.current = true;
        chatStore.loadOlderMessages(channelId);
    }, [chatStore, channelId, threadRootId]);
    useEffect(() => {
        const sentinel = sentinelRef.current;
        if (!sentinel)
            return;
        const observer = new IntersectionObserver((entries) => {
            if (entries[0].isIntersecting) {
                handleLoadOlder();
            }
        }, { threshold: 0.1 });
        observer.observe(sentinel);
        return () => observer.disconnect();
    }, [handleLoadOlder]);
    // Initial scroll to bottom
    useEffect(() => {
        const container = scrollRef.current;
        if (container && messages.length > 0) {
            container.scrollTop = container.scrollHeight;
        }
    }, [channelId, threadRootId]);
    if (isLoading && messages.length === 0) {
        return (_jsx("div", { className: styles.loadingContainer, children: _jsx(Spin, {}) }));
    }
    if (messages.length === 0) {
        return (_jsx("div", { className: styles.emptyContainer, children: _jsx(Empty, { description: "\u041D\u0435\u0442 \u0441\u043E\u043E\u0431\u0449\u0435\u043D\u0438\u0439" }) }));
    }
    const renderMessages = () => {
        const activeChannel = chatStore.activeChannel;
        const isRouteChannel = activeChannel?.type === ChannelType.ROUTE;
        const elements = [];
        let lastRenderedAt = null;
        messages.forEach((msg) => {
            // UC1003: Skip thread replies in route channels — shown as count in ThreadPreview
            if (isRouteChannel && msg.rootId) {
                return;
            }
            // Date separator
            if (lastRenderedAt === null ||
                !isSameDay(lastRenderedAt, msg.createAt)) {
                elements.push(_jsx("div", { className: styles.dateSeparator, children: _jsx(Divider, { plain: true, children: formatDateSeparator(msg.createAt) }) }, `date-${msg.createAt}`));
            }
            lastRenderedAt = msg.createAt;
            // UC1002: Bot request separators — render as styled divider
            if (msg.authorType === MessageAuthorType.BOT &&
                msg.message.includes("───── Новое обращение •")) {
                elements.push(_jsx("div", { className: styles.requestSeparator, children: _jsx(Divider, { plain: true, className: styles.requestDivider, children: _jsx("span", { className: styles.requestSeparatorText, children: msg.message.replace(/─+/g, "").trim() }) }) }, msg.id));
                return;
            }
            // UC1003: Thread root posts in route channels — render as ThreadPreview
            if (isRouteChannel && !msg.rootId) {
                const isThreadRoot = (msg.props && msg.props.orderId) ||
                    /(?:Чат с клиентом|Обсуждение заказа).*PZ-\d+/.test(msg.message);
                if (isThreadRoot) {
                    elements.push(_jsx(ThreadPreview, { rootPost: msg, replyCount: msg.replyCount, onClick: () => chatStore.openThread(msg.id) }, msg.id));
                    return;
                }
            }
            elements.push(_jsx(MessageItem, { message: msg, isOwn: msg.authorId === currentUserId }, msg.id));
        });
        return elements;
    };
    return (_jsx("div", { className: styles.container, children: _jsxs("div", { className: styles.scrollArea, ref: scrollRef, children: [_jsx("div", { ref: sentinelRef, className: styles.sentinel }), isLoading && (_jsx("div", { className: styles.loadingTop, children: _jsx(Spin, { size: "small" }) })), renderMessages()] }) }));
});
MessageList.displayName = "MessageList";
export default MessageList;
